import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Play, 
  Pause, 
  X, 
  ChevronUp, 
  ChevronDown, 
  Volume2, 
  VolumeX,
  Music,
  Video,
  Maximize2,
  Minimize2
} from 'lucide-react'
import { gameUIVariants } from '@/lib/animations'
import { useMediaPlayer } from '@/hooks/useMediaPlayer'
import { MediaPlayerProps, StickyPlayerState } from '@/types/media'
import { getMediaType, formatDuration } from '@/lib/media-utils'

interface StickyMediaPlayerProps extends MediaPlayerProps {
  isVisible: boolean
  onClose: () => void
  onToggleMinimize?: (isMinimized: boolean) => void
  position?: 'bottom' | 'top'
}

const StickyMediaPlayer: React.FC<StickyMediaPlayerProps> = ({ 
  file, 
  config = {},
  className = '',
  isVisible,
  onClose,
  onToggleMinimize,
  position = 'bottom',
  onPlayerReady,
  onPlayStateChange,
  onTimeUpdate,
  onMetadataLoad,
  onError
}) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [showVolume, setShowVolume] = useState(false)

  const stickyConfig = {
    ...config,
    mode: 'sticky' as const,
    showWaveform: !isMinimized, // Hide waveform when minimized
    showVideoControls: false // Disable video controls in sticky mode
  }

  const { state, playerRef, videoRef, waveformRef, audioRef } = useMediaPlayer({
    file,
    config: stickyConfig,
    onPlayStateChange,
    onTimeUpdate,
    onMetadataLoad,
    onError
  })

  const mediaType = getMediaType(file)
  const isVideo = mediaType === 'video'

  React.useEffect(() => {
    if (onPlayerReady && !state.isLoading) {
      onPlayerReady(playerRef)
    }
  }, [onPlayerReady, playerRef, state.isLoading])

  const handleToggleMinimize = () => {
    const newMinimized = !isMinimized
    setIsMinimized(newMinimized)
    onToggleMinimize?.(newMinimized)
  }

  const togglePlay = () => {
    if (state.isPlaying) {
      playerRef.pause()
    } else {
      playerRef.play()
    }
  }

  const toggleMute = () => {
    if (state.isMuted) {
      playerRef.setVolume(state.volume)
    } else {
      playerRef.setVolume(0)
    }
  }

  const handleVolumeChange = (newVolume: number) => {
    playerRef.setVolume(newVolume)
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (state.duration === 0) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * state.duration
    playerRef.seek(newTime)
  }

  const stickyVariants = {
    hidden: { 
      y: position === 'bottom' ? '100%' : '-100%',
      opacity: 0
    },
    visible: { 
      y: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
    exit: { 
      y: position === 'bottom' ? '100%' : '-100%',
      opacity: 0,
      transition: { duration: 0.3, ease: 'easeIn' }
    }
  }

  const expandedVariants = {
    minimized: { 
      height: 80,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
    expanded: { 
      height: 'auto',
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  }

  const MediaIcon = isVideo ? Video : Music

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`fixed ${position === 'bottom' ? 'bottom-0' : 'top-0'} left-0 right-0 z-50 ${className}`}
          variants={stickyVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="bg-cosmic-dark/95 backdrop-blur-lg border-t-2 border-stellar-cyan/30 shadow-2xl"
            variants={expandedVariants}
            animate={isMinimized ? 'minimized' : 'expanded'}
          >
            {/* Hidden audio/video elements */}
            {isVideo ? (
              <video 
                ref={videoRef}
                className="hidden"
                playsInline
                controls={false}
              />
            ) : (
              <audio 
                ref={audioRef}
                className="hidden"
                controls={false}
              />
            )}

            {/* Main Content */}
            <div className="px-4 py-3">
              {/* Minimized View */}
              {isMinimized ? (
                <div className="flex items-center justify-between">
                  {/* Left: Media Info & Play Control */}
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {/* Album Art */}
                    <div className="w-12 h-12 rounded-lg overflow-hidden border border-stellar-cyan/30 flex-shrink-0">
                      {state.metadata?.picture ? (
                        <img 
                          src={state.metadata.picture}
                          alt="Album Art"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-stellar-blue to-stellar-cyan flex items-center justify-center">
                          <MediaIcon className="h-6 w-6 text-cosmic-black" />
                        </div>
                      )}
                    </div>

                    {/* Play/Pause */}
                    <motion.button
                      onClick={togglePlay}
                      disabled={state.isLoading}
                      className="w-10 h-10 rounded-full bg-stellar-blue text-cosmic-black hover:bg-stellar-cyan transition-colors flex items-center justify-center flex-shrink-0"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {state.isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                    </motion.button>

                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">
                        {state.metadata?.title || file.name}
                      </h4>
                      <p className="text-stellar-cyan text-xs truncate">
                        {state.metadata?.artist || 'Unknown Artist'}
                      </p>
                    </div>
                  </div>

                  {/* Right: Controls */}
                  <div className="flex items-center space-x-2">
                    {/* Volume */}
                    <div className="relative">
                      <motion.button
                        onClick={() => setShowVolume(!showVolume)}
                        className="text-stellar-cyan hover:text-white p-1"
                        whileHover={{ scale: 1.1 }}
                      >
                        {state.isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                      </motion.button>

                      <AnimatePresence>
                        {showVolume && (
                          <motion.div
                            className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-cosmic-dark border border-stellar-cyan/30 rounded-lg p-2"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                          >
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={state.isMuted ? 0 : state.volume}
                              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                              className="w-20 h-2 bg-stellar-cyan/30 rounded-lg appearance-none cursor-pointer slider vertical"
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Expand */}
                    <motion.button
                      onClick={handleToggleMinimize}
                      className="text-stellar-cyan hover:text-white p-1"
                      whileHover={{ scale: 1.1 }}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </motion.button>

                    {/* Close */}
                    <motion.button
                      onClick={onClose}
                      className="text-stellar-cyan hover:text-danger-red p-1"
                      whileHover={{ scale: 1.1 }}
                    >
                      <X className="h-4 w-4" />
                    </motion.button>
                  </div>
                </div>
              ) : (
                /* Expanded View */
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-medium text-lg">Now Playing</h3>
                    <div className="flex items-center space-x-2">
                      <motion.button
                        onClick={handleToggleMinimize}
                        className="text-stellar-cyan hover:text-white p-1"
                        whileHover={{ scale: 1.1 }}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        onClick={onClose}
                        className="text-stellar-cyan hover:text-danger-red p-1"
                        whileHover={{ scale: 1.1 }}
                      >
                        <X className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>

                  {/* Waveform */}
                  {stickyConfig.showWaveform && (
                    <div className="w-full h-16 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 overflow-hidden p-2">
                      <div 
                        ref={waveformRef} 
                        className="w-full h-full"
                      />
                    </div>
                  )}

                  {/* Progress Bar */}
                  <div 
                    className="w-full bg-stellar-cyan/20 rounded-full h-2 cursor-pointer"
                    onClick={handleSeek}
                  >
                    <motion.div
                      className="bg-gradient-to-r from-stellar-blue to-stellar-cyan h-2 rounded-full"
                      style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
                      animate={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>

                  {/* Controls and Info */}
                  <div className="flex items-center justify-between">
                    {/* Left: Album Art & Info */}
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden border border-stellar-cyan/30">
                        {state.metadata?.picture ? (
                          <img 
                            src={state.metadata.picture}
                            alt="Album Art"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-stellar-blue to-stellar-cyan flex items-center justify-center">
                            <MediaIcon className="h-8 w-8 text-cosmic-black" />
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="text-white font-medium">
                          {state.metadata?.title || file.name}
                        </h4>
                        <p className="text-stellar-cyan text-sm">
                          {state.metadata?.artist && `${state.metadata.artist}`}
                          {state.metadata?.artist && state.metadata?.album && ` • `}
                          {state.metadata?.album && `${state.metadata.album}`}
                        </p>
                        <p className="text-stellar-cyan/60 text-xs">
                          {formatDuration(state.currentTime)} / {formatDuration(state.duration)}
                        </p>
                      </div>
                    </div>

                    {/* Center: Play Controls */}
                    <div className="flex items-center space-x-4">
                      <motion.button
                        onClick={togglePlay}
                        disabled={state.isLoading}
                        className="w-12 h-12 rounded-full bg-stellar-blue text-cosmic-black hover:bg-stellar-cyan transition-colors flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {state.isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                      </motion.button>
                    </div>

                    {/* Right: Volume */}
                    <div className="flex items-center space-x-3">
                      <motion.button
                        onClick={toggleMute}
                        className="text-stellar-cyan hover:text-white"
                        whileHover={{ scale: 1.1 }}
                      >
                        {state.isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                      </motion.button>
                      
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={state.isMuted ? 0 : state.volume}
                        onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        className="w-20 h-2 bg-stellar-cyan/30 rounded-lg appearance-none cursor-pointer slider"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Progress indicator when minimized */}
            {isMinimized && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-stellar-cyan/20">
                <motion.div
                  className="h-full bg-gradient-to-r from-stellar-blue to-stellar-cyan"
                  style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
                  animate={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>
            )}

            {/* Loading indicator */}
            {state.isLoading && (
              <div className="absolute inset-0 bg-cosmic-dark/80 flex items-center justify-center">
                <motion.div 
                  className="animate-spin rounded-full h-6 w-6 border-b-2 border-stellar-blue"
                  variants={gameUIVariants}
                  animate="loadingRotate"
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default StickyMediaPlayer